import {useApp} from "../app.context";
import {useDrag} from "react-dnd";
import AssetImage from "./assetImage.element";
import Checkmark from "./checkmark";
import {getMetadataDisplayInfo} from "../utils/metadata.utils";
import {useNavTarget} from "./useNavTarget";
import {NavTarget} from "../utils/navTarget";
import {MetadataCollection} from "../commands/metadataValue.command";

function MetadataDragLabel(props: {
    uid: string,
    id?: string,
    type: string,
    draggable(): boolean,
    values?: { [key: string]: number },
    entities?: { [key: string]: string },
    duplicate?: boolean,
    // Collection this metadata belongs to, used to look up its manifest entry so
    // a duplicate's suffix lists parameters in the order the form declares them.
    // Optional: without it the suffix falls back to JSON key order.
    collection?: MetadataCollection,
    // When provided, overrides the duplicate-only computed suffix. Lets callers
    // (e.g. the Rewards tab) show authored parameters inline regardless of dupes.
    suffix?: string,
    // Frees the metadata from whatever currently holds it. Omitted → no button.
    // Used by the Tech Tree palette, where the only other way to unassign is to
    // find the node holding the unlock and drag it off.
    onUnassign?(): void,
    // Tooltip on that button, e.g. "Unassign from Writing". Defaults to a bare
    // "Unassign".
    unassignTitle?: string
}) {
    const {getLocalizationValue, setNotification, getTypeDescriptionKey, getAttributeManifestEntry} = useApp();

    const [{isDragging}, drag] = useDrag({
        type: props.type,
        item: {
            id: props.uid,
        },
        collect: (monitor) => {
            return {
                isDragging: monitor.isDragging(),
            };
        },
        canDrag: props.draggable()
    });

    const {suffix: computedSuffix, tooltip} = getMetadataDisplayInfo(
        props.id ? {id: props.id, values: props.values ?? {}, entities: props.entities ?? {}} as any : undefined,
        props.duplicate ?? false,
        getLocalizationValue,
        props.id && props.collection !== undefined ? getAttributeManifestEntry(props.id, props.collection) : undefined
    );
    const suffix = props.suffix ?? computedSuffix;

    // Ctrl/Cmd-click navigates to this metadata's editor.
    const navTarget: NavTarget = {kind: 'metadata', uid: props.uid};

    // Dimmed while dragging, and whenever it can't be dragged at all — in the
    // Tech Tree palette that means it is already assigned to a node. The dim
    // lands on the row's content rather than on the row, so the Unassign button
    // keeps full strength (see .dim-row-content).
    const dimmed = isDragging || !props.draggable();

    return (
        <div ref={drag} {...useNavTarget(navTarget)}
             className={dimmed ? 'dim-row-content' : undefined} title={tooltip}>
            <div className={'layout horizontal center stretch gap label'}>
                <div className={'layout horizontal gap center'}>
                    <div style={{position: 'relative'}}>
                        <AssetImage id={props.uid}/>
                        <Checkmark id={props.uid} style={{position: 'absolute', top: 2, right: 2}}/>
                    </div>
                    <p style={{width: 160, padding: 5}}
                       onClick={() => {
                           // Description is keyed by the metadata's id so all
                           // instances of the same type (e.g. all three
                           // GoldPerTurn rewards) share one editable copy —
                           // matches MetadataLabel + MetadataElement. Fall back
                           // to uid only when the caller didn't pass an id.
                           const descKey = props.id ? getTypeDescriptionKey(props.uid, props.id) : `${props.uid}.description`;
                           setNotification({
                               title: props.uid,
                               message: descKey,
                               edit: true
                           });
                       }}
                    >
                        {getLocalizationValue(props.uid)}
                    </p>
                </div>
                {suffix && <p style={{marginLeft: 'auto', padding: 5}}>{suffix.trim()}</p>}
                {props.onUnassign && <button className={'row-action'}
                                             style={{marginLeft: suffix ? undefined : 'auto'}}
                                             title={props.unassignTitle ?? 'Unassign'}
                                             onClick={(e) => {
                                                 // The name opens the description popup on click and
                                                 // the row is a Ctrl/Cmd-click navigation target;
                                                 // neither should fire.
                                                 e.stopPropagation();
                                                 props.onUnassign?.();
                                             }}>
                    <span className={'fa-solid fa-link-slash'}/>
                </button>}
            </div>
        </div>
    );
}

export default MetadataDragLabel;
